import React, { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { useSettings } from '../contexts/SettingsContext';
import { Button } from './ui/Button';
import TextInput from './ui/TextInput';
import { Label } from './ui/Label';
import { cn } from '../lib/utils';
import { APP_BRAND } from '../config/branding';
import AppBrandIcon from './branding/AppBrandIcon';
import Login2FA from './Login2FA';
import { useDocumentTitle } from '../hooks/useDocumentTitle';

const COMM_FEATURES = [
  { labelKey: 'featureEmail', icon: 'M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z' },
  {
    labelKey: 'featureAddressBook',
    icon: 'M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z',
  },
  {
    labelKey: 'featureAppointments',
    icon: 'M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z',
  },
  {
    labelKey: 'featureTodos',
    icon: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4',
  },
] as const;

const Login: React.FC = () => {
  const { t } = useTranslation('login');
  const [searchParams] = useSearchParams();
  const registered = searchParams.get('registered') === '1';
  const passwordReset = searchParams.get('reset') === '1';
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [pending2faToken, setPending2faToken] = useState<string | null>(null);
  const [emailError, setEmailError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const { colors } = useTheme();
  const { login } = useAuth();
  const { appConfig } = useSettings();

  const productName = appConfig.system.appName?.trim() || APP_BRAND.name;

  useDocumentTitle(pending2faToken ? t('twoFactorTitle') : t('signIn'));

  const validateEmail = (value: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(value);
  };

  const validateForm = (): boolean => {
    let isValid = true;
    setEmailError('');
    setPasswordError('');

    if (!email) {
      setEmailError(t('emailRequired'));
      isValid = false;
    } else if (!validateEmail(email)) {
      setEmailError(t('emailInvalid'));
      isValid = false;
    }

    if (!password) {
      setPasswordError(t('passwordRequired'));
      isValid = false;
    } else if (password.length < 6) {
      setPasswordError(t('passwordMinLength'));
      isValid = false;
    }

    return isValid;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!validateForm()) return;

    setIsLoading(true);
    try {
      const result = await login({ email, password });
      if (result?.requires2fa && result.pendingToken) {
        setPending2faToken(result.pendingToken);
        return;
      }
    } catch (err: unknown) {
      const message =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: { message?: string } } }).response?.data?.message
          : undefined;
      setError(message || t('invalidCredentials'));
    } finally {
      setIsLoading(false);
    }
  };

  if (pending2faToken) {
    return (
      <div className="min-h-screen flex flex-col lg:flex-row">
        <div className="relative hidden flex-col justify-between overflow-hidden bg-gradient-to-br from-slate-900 via-sky-950 to-indigo-950 px-8 py-10 lg:flex lg:w-[44%] lg:min-h-screen lg:px-12 lg:py-14" />
        <Login2FA pendingToken={pending2faToken} onBack={() => setPending2faToken(null)} />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col lg:flex-row">
      {/* Brand panel */}
      <div className="relative flex flex-col justify-between overflow-hidden bg-gradient-to-br from-slate-900 via-sky-950 to-indigo-950 px-8 py-10 lg:w-[44%] lg:min-h-screen lg:px-12 lg:py-14">
        <div
          className="pointer-events-none absolute inset-0 opacity-40"
          aria-hidden
          style={{
            backgroundImage:
              'radial-gradient(circle at 20% 20%, rgba(56,189,248,0.35) 0%, transparent 45%), radial-gradient(circle at 80% 70%, rgba(99,102,241,0.3) 0%, transparent 40%)',
          }}
        />
        <div className="relative z-10">
          <div className="flex items-center gap-4">
            <AppBrandIcon className="h-12 w-12" iconClassName="h-6 w-6" />
            <div>
              <h1 className="text-2xl font-bold tracking-tight text-white">{productName}</h1>
            </div>
          </div>
          <p className="mt-8 max-w-md text-base leading-relaxed text-slate-300">
            {t('tagline')}
          </p>
          <ul className="mt-8 grid grid-cols-2 gap-3 sm:max-w-md">
            {COMM_FEATURES.map(({ labelKey, icon }) => (
              <li
                key={labelKey}
                className="flex items-center gap-2.5 rounded-xl border border-white/10 bg-white/5 px-3 py-2.5 text-sm text-slate-100 backdrop-blur-sm"
              >
                <svg className="h-4 w-4 shrink-0 text-sky-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden>
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d={icon} />
                </svg>
                {t(labelKey)}
              </li>
            ))}
          </ul>
        </div>
        <p className="relative z-10 mt-10 text-xs text-slate-500 lg:mt-0">
          © {new Date().getFullYear()} {productName}.
        </p>
      </div>

      {/* Sign-in panel */}
      <div className={cn('flex flex-1 items-center justify-center px-4 py-10 sm:px-8', colors.background)}>
        <div className="w-full max-w-md">
          <div className="mb-8 lg:hidden">
            <div className="flex items-center gap-3">
              <AppBrandIcon className="h-11 w-11" iconClassName="h-5 w-5" />
              <div>
                <p className={cn('text-lg font-bold', colors.text)}>{productName}</p>
              </div>
            </div>
          </div>

          <div className={cn('rounded-2xl border p-8 shadow-sm', colors.surface, colors.border)}>
            <div className="mb-8">
              <h2 className={cn('text-2xl font-semibold tracking-tight', colors.text)}>{t('signIn')}</h2>
              <p className={cn('mt-1 text-sm', colors.textMuted)}>{t('signInSubtitle')}</p>
            </div>

            <form className="space-y-5" onSubmit={handleSubmit}>
              {registered && (
                <div className={cn('rounded-lg border px-4 py-3 text-sm', colors.success)} role="status">
                  {t('accountCreated')}
                </div>
              )}
              {passwordReset && (
                <div className={cn('rounded-lg border px-4 py-3 text-sm', colors.success)} role="status">
                  {t('passwordResetSuccess')}
                </div>
              )}
              {error && (
                <div className={cn('rounded-lg border px-4 py-3 text-sm', colors.error)} role="alert">
                  {error}
                </div>
              )}

              <div className="space-y-4">
                <div>
                  <Label htmlFor="email">{t('email')}</Label>
                  <TextInput
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    placeholder="you@company.com"
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      if (emailError) setEmailError('');
                    }}
                    className="mt-1.5"
                    aria-invalid={!!emailError}
                  />
                  {emailError && <p className="mt-1 text-sm text-red-500">{emailError}</p>}
                </div>

                <div>
                  <div className="flex items-center justify-between">
                    <Label htmlFor="password">{t('password')}</Label>
                    <Link
                      to="/forgot-password"
                      className="text-xs font-medium text-indigo-600 hover:text-indigo-700"
                    >
                      {t('forgotPassword')}
                    </Link>
                  </div>
                  <TextInput
                    id="password"
                    name="password"
                    type="password"
                    autoComplete="current-password"
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => {
                      setPassword(e.target.value);
                      if (passwordError) setPasswordError('');
                    }}
                    className="mt-1.5"
                    aria-invalid={!!passwordError}
                  />
                  {passwordError && <p className="mt-1 text-sm text-red-500">{passwordError}</p>}
                </div>
              </div>

              <Button type="submit" className="w-full" loading={isLoading} disabled={isLoading}>
                {t('signInButton', { productName })}
              </Button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
